import { Reservation } from "@workspace/domain/src/features/reservation";
import { errorMessage } from "@workspace/domain/src/utils";
import Decimal from "decimal.js";
import * as z from "zod";
import { AdminTrip } from "../trip/schema";

const PaymentSchema = z.object({
  id: z.string(),
  status: z.enum(Reservation.PaymentStatus),
  amount: z.number(),
  sourceSubType: z.string().nullable(),
  sourcePan: z.string().nullable(),
  transactionId: z.string().nullable(),
  reservationId: z.string(),
  createdAt: z.date(),
});

const ReservationSchema = z.object({
  id: z.string(),

  serial: z.string(),
  status: z.enum(Reservation.Status),
  cancelledAt: z.date().nullable(),

  // Trip information
  groupPricing: AdminTrip.Trip.shape.groupPricing,
  adultPrice: z.number(),
  adultReservationFee: z.number(),
  adultCancellationFee: z.number(),
  childPrice: z.number(),
  childReservationFee: z.number(),
  childCancellationFee: z.number(),

  cancellationDeadline: z.date().nullable(),

  numberOfAdults: z.number(),
  numberOfChildren: z.number(),

  totalPrice: z.number(),
  totalReservationFee: z.number(),
  totalCancellationFee: z.number(),
  totalRefundable: z.number(),

  totalPaid: z.number(),
  totalRefunded: z.number(),

  amountDue: z.number(),
  refundDue: z.number(),

  tripId: z.string(),
  agencyClientId: z.string(),
  createdById: z.string().nullable(),

  createdAt: z.date(),
  updatedAt: z.date(),
});

const GetSchema = z.object({
  type: z.enum(["reservationId", "paymentId"]),
  value: z.string(),
});

const GetAllSchema = z.union([
  z.object({
    value: z.string(),
    type: z.enum(["agencyClientId", "tripId", "discountCodeId"]),
  }),
  z.object({
    value: z.null(),
    type: z.literal("all"),
  }),
]);

const BaseSchema = z.object({
  tripId: z.string(),
  agencyClientId: z.string(errorMessage("Client is required", "Client is invalid")),

  numberOfAdults: z
    .number(errorMessage("Number of adults is required", "Number of adults is invalid"))
    .min(1, "Number of adults must be at least 1")
    .max(100, "Number of adults must be less than 100"),
  numberOfChildren: z
    .number(errorMessage("Number of children is required", "Number of children is invalid"))
    .min(0, "Number of children must be at least 0")
    .max(100, "Number of children must be less than 100"),

  totalPrice: z.number(),
  totalReservationFee: z.number(),
  totalCancellationFee: z.number(),

  totalPaid: z
    .number(errorMessage("Total paid is required", "Total paid is invalid"))
    .min(0, "Total paid must be at least 0")
    .transform((val) => new Decimal(val).times(100).toNumber()),
  totalRefunded: z
    .number(errorMessage("Total refunded is required", "Total refunded is invalid"))
    .min(0, "Total refunded must be at least 0")
    .transform((val) => new Decimal(val).times(100).toNumber()),

  cancelled: z.boolean(errorMessage("Cancelled is required", "Cancelled is invalid")),

  force: z.boolean().optional(),
});

const CreateSchema = BaseSchema;

const UpdateSchema = BaseSchema.extend({
  id: z.string(),
}).omit({
  totalPaid: true,
  totalRefunded: true,
});

const AddPaymentSchema = z.object({
  reservationId: z.string(),
  amount: z
    .number(errorMessage("Amount is required", "Amount is invalid"))
    .min(1, "Amount must be greater than 0")
    .transform((val) => new Decimal(val).times(100).toNumber()),
  status: z.enum(["REFUNDED", "PAID"], errorMessage("Status is required", "Status is invalid")),
});

const UpdatePaymentSchema = AddPaymentSchema.extend({
  id: z.string(),
});

const VoidPaymentSchema = z.object({
  id: z.string(),
});

export const AdminReservation = {
  Reservation: ReservationSchema,
  Payment: PaymentSchema,
  Get: GetSchema,
  GetAll: GetAllSchema,
  Create: CreateSchema,
  Update: UpdateSchema,
  AddPayment: AddPaymentSchema,
  UpdatePayment: UpdatePaymentSchema,
  VoidPayment: VoidPaymentSchema,
};
export type AdminReservation = {
  Reservation: z.infer<typeof ReservationSchema>;
  Payment: z.infer<typeof PaymentSchema>;
  Get: z.infer<typeof GetSchema>;
  GetAll: z.infer<typeof GetAllSchema>;
  Create: z.infer<typeof CreateSchema>;
  Update: z.infer<typeof UpdateSchema>;
  AddPayment: z.infer<typeof AddPaymentSchema>;
  UpdatePayment: z.infer<typeof UpdatePaymentSchema>;
  VoidPayment: z.infer<typeof VoidPaymentSchema>;
};
